import axios, { AxiosError } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import dayjs from 'dayjs';

import { getCurrentI18nLanguage } from '../../i18n';
import { resolveSupportedLanguage } from '../../i18n/languages';
import { safeLocalStorage } from '../../utils/safeLocalStorage';
import {
  AccountApi,
  CalendarApi,
  ChatApi,
  DataTrendApi,
  EnergyApi,
  ErrorRes,
  ExternalApi,
  IntegrationsApi,
  MetricsApi,
  SchedulerApi,
  TeamApi,
  TasksApi,
  SharedAccessApi,
} from './openapi';
import { isDesktopApp } from '../../utils/environment';

export const axiosInstance = axios.create();

export const API_URL = process.env.REACT_APP_API_URL;
const JWT_401_ERROR_REASON = 'jwt';

export const accountApi = new AccountApi(undefined, API_URL, axiosInstance);
export const chatApi = new ChatApi(undefined, API_URL, axiosInstance);
export const calendarApi = new CalendarApi(undefined, API_URL, axiosInstance);
export const energyApi = new EnergyApi(undefined, API_URL, axiosInstance);
export const integrationsApi = new IntegrationsApi(undefined, API_URL, axiosInstance);
export const metricsApi = new MetricsApi(undefined, API_URL, axiosInstance);
export const schedulerApi = new SchedulerApi(undefined, API_URL, axiosInstance);
export const tasksApi = new TasksApi(undefined, API_URL, axiosInstance);
export const teamApi = new TeamApi(undefined, API_URL, axiosInstance);
export const externalApi = new ExternalApi(undefined, API_URL, axiosInstance);
export const dataTrendApi = new DataTrendApi(undefined, API_URL, axiosInstance);
export const sharedAccessApi = new SharedAccessApi(undefined, API_URL, axiosInstance);

export function getOAuthAccessToken(): string | undefined {
  return safeLocalStorage.getItem('lifestack_access_token') ?? undefined;
}

export function getOAuthRefreshToken(): string | undefined {
  return safeLocalStorage.getItem('lifestack_refresh_token') ?? undefined;
}

export function setOAuthRefreshToken(token: string): void {
  safeLocalStorage.setItem('lifestack_refresh_token', token);
}

export function setOAuthAccessToken(token: string): void {
  safeLocalStorage.setItem('lifestack_access_token', token);
}

export function clearOAuthAccessToken(): void {
  safeLocalStorage.removeItem('lifestack_access_token');
}

export async function refreshOAuthAccessToken(): Promise<string | null> {
  const refresh = getOAuthRefreshToken();
  if (!refresh) {
    clearOAuthAccessToken();
    return null;
  }

  const privateAccountApi = new AccountApi(undefined, API_URL, axios.create());

  try {
    const { data } = await privateAccountApi.refreshToken(refresh);
    if (!data?.access) {
      clearOAuthAccessToken();
      return null;
    }

    setOAuthAccessToken(data.access);
    return data.access;
  } catch (error: any) {
    if (error?.response?.status === 403) {
      setOAuthRefreshToken('');
      clearOAuthAccessToken();
      safeLocalStorage.removeItem('lifestack_user_id');
      window.location.reload();
    }
    throw error;
  }
}

export async function ensureAccessTokenAlive(): Promise<boolean> {
  const accessToken = getOAuthAccessToken();
  if (!accessToken) {
    const refreshedToken = await refreshOAuthAccessToken();
    return Boolean(refreshedToken);
  }

  try {
    const { data } = await accountApi.checkAccessAlive();
    if (data?.alive) {
      return true;
    }
    clearOAuthAccessToken();
    const refreshedToken = await refreshOAuthAccessToken();
    return Boolean(refreshedToken);
  } catch (error: any) {
    if (error?.response?.status === 401 && error?.response?.data?.reason === 'jwt') {
      clearOAuthAccessToken();
      const refreshedToken = await refreshOAuthAccessToken();
      return Boolean(refreshedToken);
    }
    throw error;
  }
}

axiosInstance.interceptors.request.use(
  async (config) => {
    const oAuthAccessToken = await getOAuthAccessToken();
    const userId = safeLocalStorage.getItem('lifestack_user_id');
    const newConfig = { ...config };

    newConfig.headers['Content-Type'] = 'application/json';
    newConfig.headers['X-Device-Type'] = isDesktopApp() ? 'desktop' : 'web';
    newConfig.headers['X-Time-Zone'] = dayjs.tz.guess();
    newConfig.headers['X-User-Id'] = userId;
    newConfig.headers['Accept-Language'] = resolveSupportedLanguage(getCurrentI18nLanguage());

    if (oAuthAccessToken) {
      newConfig.headers.Authorization = `Bearer ${oAuthAccessToken}`;
    }

    return newConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const refreshAuthLogic = (failedRequest: AxiosError) => {
  const errorReason = (failedRequest.response?.data as ErrorRes)?.reason;
  if (errorReason !== JWT_401_ERROR_REASON) {
    return Promise.reject(failedRequest);
  }
  const refresh = getOAuthRefreshToken();

  // Need to create a new instance for axios to call endpoints
  const privateAccountApi = new AccountApi(undefined, API_URL, axios.create());

  return privateAccountApi
    .refreshToken(refresh!)
    .then(async ({ data }) => {
      if (!data) {
        setOAuthRefreshToken('');
        return;
      }
      const { access: accessToken } = data;
      setOAuthAccessToken(accessToken);

      if (failedRequest.response) {
        failedRequest.response.config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return Promise.resolve();
    })
    .catch(async (error) => {
      // Only when the refresh token is invalid (the endpoint returns 403 error), clear the refresh token
      if (error?.response?.status === 403) {
        setOAuthRefreshToken('');
        setOAuthAccessToken('');
        safeLocalStorage.removeItem('lifestack_user_id');
        window.location.reload();
      }
    });
};

createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic, {
  statusCodes: [401],
});

export * from './openapi';
